import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Buildings,
  MagnifyingGlass,
  TreeStructure,
  SquaresFour,
  FlowArrow,
  Cpu,
  GraduationCap,
  Presentation,
  Gear,
  SignOut,
  User as UserIcon,
  BookOpen,
} from "@phosphor-icons/react";

const VIEW_MODES = {
  ORG: "org",
  FUNCTION: "function",
  PROCESS: "process",
  SYSTEM: "system",
};

export default function Header({
  user,
  onLogout,
  viewMode,
  setViewMode,
  trainingMode,
  setTrainingMode,
  presentationMode,
  setPresentationMode,
  onSearchOpen,
  isAdmin,
  onAdminClick,
  onStartTour,
}) {
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="glass-header border-b border-black/10 sticky top-0 z-50" data-testid="header">
      <div className="flex items-center justify-between px-4 md:px-6 h-16">
        {/* Logo and Title */}
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-md">
            <Buildings size={24} weight="bold" className="text-white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg font-bold text-foreground tracking-tight">CME Operating Map</h1>
            <p className="text-xs text-muted-foreground -mt-0.5">Organization Workflow System</p>
          </div>
        </div>

        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="hidden md:block">
          <TabsList className="bg-secondary/50 p-1">
            <TabsTrigger
              value={VIEW_MODES.ORG}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="view-tab-org"
            >
              <TreeStructure size={16} className="mr-1.5" />
              Org
            </TabsTrigger>
            <TabsTrigger
              value={VIEW_MODES.FUNCTION}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="view-tab-function"
            >
              <SquaresFour size={16} className="mr-1.5" />
              Function
            </TabsTrigger>
            <TabsTrigger
              value={VIEW_MODES.PROCESS}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="view-tab-process"
            >
              <FlowArrow size={16} className="mr-1.5" />
              Process
            </TabsTrigger>
            <TabsTrigger
              value={VIEW_MODES.SYSTEM}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm"
              data-testid="view-tab-system"
            >
              <Cpu size={16} className="mr-1.5" />
              System
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <Button
            variant="outline"
            size="sm"
            onClick={onSearchOpen}
            className="hidden sm:flex items-center gap-2 text-muted-foreground hover:text-foreground"
            data-testid="search-btn"
          >
            <MagnifyingGlass size={16} />
            <span className="hidden lg:inline">Search</span>
            <kbd className="hidden lg:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              ⌘K
            </kbd>
          </Button>

          {/* Training Mode */}
          <Button
            variant={trainingMode ? "default" : "outline"}
            size="sm"
            onClick={() => setTrainingMode(!trainingMode)}
            className={trainingMode ? "bg-accent text-accent-foreground hover:bg-accent/90" : ""}
            data-testid="training-mode-btn"
          >
            <GraduationCap size={16} className="mr-1.5" />
            <span className="hidden sm:inline">Training</span>
          </Button>

          {/* Guided Tour */}
          {onStartTour && (
            <Button
              variant="outline"
              size="sm"
              onClick={onStartTour}
              data-testid="start-tour-btn"
            >
              <BookOpen size={16} className="mr-1.5" />
              <span className="hidden sm:inline">Tour</span>
            </Button>
          )}

          {/* Presentation Mode */}
          <Button
            variant={presentationMode ? "default" : "outline"}
            size="sm"
            onClick={() => setPresentationMode(!presentationMode)}
            data-testid="presentation-mode-btn"
          >
            <Presentation size={16} className="mr-1.5" />
            <span className="hidden sm:inline">Present</span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 ml-2" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <div className="flex items-center gap-1">
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                      {user?.is_super_admin ? "Super Admin" : user?.role}
                    </Badge>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user?.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <UserIcon size={16} className="mr-2" />
                Profile
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={onAdminClick} data-testid="admin-panel-link">
                  <Gear size={16} className="mr-2" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} className="text-destructive" data-testid="logout-btn">
                <SignOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile View Tabs */}
      <div className="md:hidden px-4 pb-2">
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="w-full bg-secondary/50 p-1">
            <TabsTrigger value={VIEW_MODES.ORG} className="flex-1 text-xs" data-testid="view-tab-org-mobile">
              <TreeStructure size={14} className="mr-1" />
              Org
            </TabsTrigger>
            <TabsTrigger value={VIEW_MODES.FUNCTION} className="flex-1 text-xs" data-testid="view-tab-function-mobile">
              <SquaresFour size={14} className="mr-1" />
              Func
            </TabsTrigger>
            <TabsTrigger value={VIEW_MODES.PROCESS} className="flex-1 text-xs" data-testid="view-tab-process-mobile">
              <FlowArrow size={14} className="mr-1" />
              Proc
            </TabsTrigger>
            <TabsTrigger value={VIEW_MODES.SYSTEM} className="flex-1 text-xs" data-testid="view-tab-system-mobile">
              <Cpu size={14} className="mr-1" />
              Sys
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    </header>
  );
}
